import { MATCH_STATE } from '../../domain/machine';
import type { MatchState, TemplateEntry, TemplateRow } from '../../types';

/**
 * B-13 — the flags review's **pure decisions** (design spec §2.4; scenario 15). DOM-free so the
 * Node suite drives every arm (§12's grammar); the surface component is glue over these plus the
 * engine's own facade (`loadFlagsContext` / `flagMinuteLabel` / the three `ResolveFlag` writers —
 * imported there, never re-derived here).
 */

/** One promote-from-library offer: a slot-free entry with the template it came from. */
export interface LibraryOfferB {
  readonly templateId: string;
  readonly templateName: string;
  readonly entry: TemplateEntry;
}

/**
 * §10.1 (b) — the library entries a flag can be promoted through: **slot-free only**.
 *
 * An entry carrying a PRE-4 slot (`{shirt}`) needs the number wheel, which is the add sheet's;
 * promoting an unresolved one would put a literal `{shirt}` into a pattern the user reads aloud at
 * full time. Filtered out rather than rendered-and-refused — a control that exists only to reject
 * its own tap is a mistap manufactured on chrome (A's flags sheet applies the same rule inline;
 * the engine has no such helper, so these four lines are B's — a duplication-ledger disposition
 * candidate, coincidental boilerplate).
 */
export function slotFreeEntriesB(library: readonly TemplateRow[]): readonly LibraryOfferB[] {
  return library.flatMap((template) =>
    template.hypothesisTexts
      .filter((entry) => (entry.slots ?? []).length === 0)
      .map((entry) => ({ templateId: template.id, templateName: template.name, entry })),
  );
}

/**
 * The empty queue's one honest sentence, total over `MatchState` (the `HALF_LABEL_B` discipline:
 * a tenth state breaks this build instead of rendering a wrong claim at a venue).
 *
 * Pre-kickoff is the state that earns its own copy — §6.4a's negative said where the finger is:
 * no flag can exist yet because the flag control refuses before kickoff, and the sentence says so
 * rather than implying the user missed something. Every reviewable state shares the in-match
 * sentence (A's queue empty-state reasoning: "nothing was missed — flags only appear here when
 * you press the Flag bar"). The post-match rows exist only for totality — the takeover hands off
 * before any of them stays on screen.
 */
const EMPTY_PRE_KICKOFF =
  'No flags yet — the match has not kicked off. Press Flag during play to mark a moment.';
const EMPTY_IN_MATCH =
  'No flags to review. Nothing was missed — flags only appear here when you press Flag.';

const EMPTY_COPY_B: Record<MatchState, string> = {
  [MATCH_STATE.idle]: EMPTY_PRE_KICKOFF,
  [MATCH_STATE.preMatch]: EMPTY_PRE_KICKOFF,
  [MATCH_STATE.firstHalf]: EMPTY_IN_MATCH,
  [MATCH_STATE.halftime]: EMPTY_IN_MATCH,
  [MATCH_STATE.secondHalf]: EMPTY_IN_MATCH,
  [MATCH_STATE.fullTime]: EMPTY_IN_MATCH,
  [MATCH_STATE.triage]: EMPTY_IN_MATCH,
  [MATCH_STATE.distill]: EMPTY_IN_MATCH,
  [MATCH_STATE.archived]: EMPTY_IN_MATCH,
};

export function flagsEmptyCopyB(state: MatchState): string {
  return EMPTY_COPY_B[state];
}
